import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Empty, Icon, Spin } from 'antd';
import REDUCER_KEY from 'reducers/reducer.keys';
import { Inner } from 'components/Page';
import { list } from 'actions';
import { canViewOptimiser } from 'library/optimiser';
import config from './config';

/*
 ** @description Prepare data for api
 ** @param props Object object_id, collection, tag_value, start_date, expiry_date, theme
 ** @return data object
 */
const handlePrepareData = ({
  objectId: object_id, tag_collection, tag_value, start_date, expiry_date, theme
}) => ({
  object_id,
  theme,
  collection_id: tag_collection.key,
  tag_id: tag_value.key,
  start_date,
  expiry_date
});

const mapStateToProps = state => ({
  inFlight: state.api.themes.inFlight,
  themes: state.api.themes.data.results
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadTheme() {
    const page = 0;
    const pageSize = 1;
    const filters = [{
      id: 'model',
      value: ownProps.themeName
    }];
    const sorted = [{
      id: 'model',
      desc: false
    }];

    return dispatch(list(
      REDUCER_KEY.themes.key, {
        page,
        pageSize,
        filters,
        sorted
      }
    ));
  }
});

const OPTIMISER_LEGEND = <div style={{ textAlign: 'right', padding: '4px 0' }}><span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>** = Used by Optimiser</span></div>;

const getFilteredConfig = () => {
  const optimiserEnabled = canViewOptimiser();
  if (optimiserEnabled) return { config, legend: OPTIMISER_LEGEND };

  const filteredColumns = config.list.columns
    .filter(col => col.dataIndex !== 'tag__collection__used_by_optimiser')
    .map((col) => {
      if (col.dataIndex === 'tag__collection__name') {
        return { ...col, render: (text, record) => record.tag.collection.name };
      }
      return col;
    });

  return {
    config: { ...config, list: { ...config.list, columns: filteredColumns } },
    legend: null
  };
};

const ItemTags = ({
  inFlight, loadTheme, objectId, themes, themeName
}) => {
  useEffect(() => {
    loadTheme();
  }, []);
  if (!themes.length) {
    return (
      <div className="bhp-container">
        <Spin indicator={<Icon type="loading" spin />} tip="Loading..." spinning={inFlight}>
          <Empty
            image="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNDEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMCAxKSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgIDxlbGxpcHNlIGZpbGw9IiNGNUY1RjUiIGN4PSIzMiIgY3k9IjMzIiByeD0iMzIiIHJ5PSI3Ii8+CiAgICA8ZyBmaWxsLXJ1bGU9Im5vbnplcm8iIHN0cm9rZT0iI0Q5RDlEOSI+CiAgICAgIDxwYXRoIGQ9Ik01NSAxMi43Nkw0NC44NTQgMS4yNThDNDQuMzY3LjQ3NCA0My42NTYgMCA0Mi45MDcgMEgyMS4wOTNjLS43NDkgMC0xLjQ2LjQ3NC0xLjk0NyAxLjI1N0w5IDEyLjc2MVYyMmg0NnYtOS4yNHoiLz4KICAgICAgPHBhdGggZD0iTTQxLjYxMyAxNS45MzFjMC0xLjYwNS45OTQtMi45MyAyLjIyNy0yLjkzMUg1NXYxOC4xMzdDNTUgMzMuMjYgNTMuNjggMzUgNTIuMDUgMzVoLTQwLjFDMTAuMzIgMzUgOSAzMy4yNTkgOSAzMS4xMzdWMTNoMTEuMTZjMS4yMzMgMCAyLjIyNyAxLjMyMyAyLjIyNyAyLjkyOHYuMDIyYzAgMS42MDUgMS4wMDUgMi45MDEgMi4yMzcgMi45MDFoMTQuNzUyYzEuMjMyIDAgMi4yMzctMS4zMDggMi4yMzctMi45MTN2LS4wMDd6IiBmaWxsPSIjRkFGQUZBIi8+CiAgICA8L2c+CiAgPC9nPgo8L3N2Zz4K"
            description="Please select a record"
          />
        </Spin>
      </div>
    );
  }
  const { config: filteredConfig, legend } = getFilteredConfig();
  return (
    <Inner
      canAdmin
      reducer={{
        item: REDUCER_KEY.sharedItemTag,
        list: REDUCER_KEY.sharedItemTags
      }}
      config={{
        ...filteredConfig,
        list: {
          ...filteredConfig.list,
          footer: legend,
          filters: {
            object_id: [objectId],
            theme: themes.length ? [themes[0].id] : [-1]
          }
        },
        detail: {
          ...filteredConfig.detail,
          parent: {
            objectId,
            theme: themes.length ? themes[0].id : -1
          }
        }
      }}
      defaultSize="280px"
      onPrepareData={handlePrepareData}
      scroll={{
        x: 800,
        y: '100%'
      }}
      messaging={{
        delete: {
          disabled: () => 'Item Disabled.',
          failure: error => `Delete Failed. ${error}`,
          success: () => 'Delete Successful.'
        },
        save: {
          failure: () => 'Saving failed',
          success: () => 'Saving was successful'
        }
      }}
      themeName={themeName}
    />
  );
};

ItemTags.propTypes = {
  inFlight: PropTypes.bool,
  loadTheme: PropTypes.func,
  objectId: PropTypes.number,
  themeName: PropTypes.string,
  themes: PropTypes.array
};

ItemTags.defaultProps = {
  inFlight: false,
  loadTheme() {
  },
  objectId: null,
  themeName: '',
  themes: []
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemTags);
